/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { NewCoreIconProps } from '@atlaskit/icon/types';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common/constants';
import { type ManifestEditorToolbarActions } from '@atlaskit/native-embeds-common/types';
import { createEditorToolbarActions } from '@atlaskit/native-embeds-common/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core/types/features/main';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import { getInitialThemeParam } from '@atlassian/native-embeds-core/theme';
import {
	type Experience,
	type ExperienceManifest,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

import { ARTIFACTS_EXPERIENCE_MANIFEST_ID } from './manifest-id';

export { ARTIFACTS_EXPERIENCE_MANIFEST_ID } from './manifest-id';

const artifactsIcons = [ShapesIcon];

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/**
 * Claims Artifacts app share URLs in either of two forms:
 *
 *   `<host>/artifacts/<artifactId>/share`
 *   `<host>/apps/<appId>/<artifactId>/share`
 *
 * where each id segment is a UUID. Examples:
 *   https://your-tenant.atlassian.net/artifacts/51599f19-d5ae-41b1-a238-ee912b1c78b8/share
 *   https://your-tenant.atlassian.net/apps/539114cb-7d5a-40ad-b74f-3ae11c4dfd72/51599f19-d5ae-41b1-a238-ee912b1c78b8/share
 *
 * The trailing `(?:[/?#]|$)` (rather than `/?$`) keeps the match working when the URL
 * carries a query string or hash, e.g. `/share?nativeEmbedView=true`.
 */
const ARTIFACTS_SHARE_URL_PATTERN = new RegExp(
	`/(?:artifacts/${UUID}|apps/${UUID}/${UUID})/share(?:[/?#]|$)`,
);

const isArtifactsUrl = (url: string): boolean =>
	// Gated behind the Forge UI artifact Confluence integration flag: the Artifacts
	// experience only claims URLs when the flag is enabled.
	fg('platform_forge_ui_artifact_confluence_integration') && ARTIFACTS_SHARE_URL_PATTERN.test(url);

/**
 * Experience manifest for an Artifacts embed.
 * Registered via `getNativeEmbedFacade().setup({ experiences: [...] })` in
 * `@atlassian/native-embeds-editor-extension`.
 */
export const ArtifactsExperienceManifest: {
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly resolveViaLinkingPlatform: true;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		// eslint-disable-next-line @typescript-eslint/ban-types
		readonly editorToolbarHandlers: {};
		// eslint-disable-next-line @typescript-eslint/ban-types
		readonly headerControls: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly useResolvedTitleIcon: true;
		readonly waitForContentReady: true;
	};
} = {
	id: ARTIFACTS_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {
		// No Artifacts-specific child commands yet.
	},
	isUrlMatch: isArtifactsUrl,
	isActive: (): true => true,
	// Route through the Smart Card / linking-platform resolver so the embed renders
	// via `<Card appearance="embed">` (EmbedFrameWrapper uses `useBasicIframe: false`).
	// Requires the Object Resolver Service to resolve Artifacts share URLs and return a
	// `preview`. Set to `false` to render a basic iframe directly from `transformUrl`.
	resolveViaLinkingPlatform: true,
	supportedFeatures: new Set<NativeEmbedFeature>([...allNativeEmbedCoreFeatures]),
	transformUrl: (url: string, isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		if (!isFullScreen) {
			urlObj.searchParams.set('hideBorder', 'true');
		}
		// Identifies the native embed view to the Artifacts app.
		urlObj.searchParams.set('nativeEmbedView', 'true');

		const initialTheme = getInitialThemeParam();
		if (initialTheme) {
			urlObj.searchParams.set('themeState', initialTheme);
		}

		return urlObj.toString();
	},
	uiConfig: {
		// Header edit button intentionally disabled for now. Re-add an `editButton`
		// entry here (using `swapArtifactsRoute`) to restore the inline edit flow.
		headerControls: {},
		editorToolbarActions: (): ManifestEditorToolbarActions<any> =>
			createEditorToolbarActions({
				items: [
					BUILTIN_TOOLBAR_KEYS.EMBED,
					BUILTIN_TOOLBAR_KEYS.EDIT,
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				],
				moreItems: [
					BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
					BUILTIN_TOOLBAR_KEYS.COPY,
					BUILTIN_TOOLBAR_KEYS.DELETE,
				],
			}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={artifactsIcons}
				testId={props.testId}
			/>
		),
		titleIcon: ShapesIcon,
		// Use the linking-platform-resolved icon (the artifact's own icon) in the
		// header, falling back to ShapesIcon until it resolves.
		useResolvedTitleIcon: true,
		appearance: 'overlay',
		waitForContentReady: true,
	},
} as const satisfies ExperienceManifest;
